import type { GridImportColumn, ImportRowError } from '../types'

export const UNIVER_HEADER_ROW_COUNT = 1

/** Univer 默认工作表总行数（含表头） */
export const UNIVER_DEFAULT_SHEET_ROW_COUNT = 1000

/** 计算预览表格可读取的数据行数，避免超出 Univer 工作表边界 */
export function resolveScanRowCount(
  dataRowCount: number,
  sheetRowCount = UNIVER_DEFAULT_SHEET_ROW_COUNT
) {
  const maxDataRows = Math.max(sheetRowCount - UNIVER_HEADER_ROW_COUNT, 1)
  const desired = Math.max(dataRowCount + 50, 100)
  return Math.min(desired, maxDataRows)
}

export function formatImportColumnHeader(column: GridImportColumn): string {
  const raw = String(column.title || column.field).trim()
  const base = raw
    .replace(/^\*+\s*/u, '')
    .replace(/\*+$/u, '')
    .trim()
  if (!base) return raw
  // 必填用前缀 *，避免与 Excel 表头已有前缀叠成「*标题*」
  return column.required ? `*${base}` : base
}

export function columnIndexToLetters(index: number): string {
  let n = index
  let result = ''
  while (n >= 0) {
    result = String.fromCharCode((n % 26) + 65) + result
    n = Math.floor(n / 26) - 1
  }
  return result
}

export function buildSheetRangeNotation(
  rowCount: number,
  colCount: number
): string {
  if (rowCount <= 0 || colCount <= 0) return 'A1'
  const endCol = columnIndexToLetters(colCount - 1)
  return `A1:${endCol}${rowCount}`
}

export function buildDataRangeNotation(
  dataRowCount: number,
  colCount: number
): string {
  if (dataRowCount <= 0 || colCount <= 0) return 'A2'
  const endCol = columnIndexToLetters(colCount - 1)
  const endRow = dataRowCount + UNIVER_HEADER_ROW_COUNT
  return `A2:${endCol}${endRow}`
}

export function cellValueFromRow(
  row: Record<string, unknown>,
  field: string
): string | number {
  const value = row[field]
  if (value == null || value === '') return ''
  if (typeof value === 'number') return value
  return String(value)
}

export function rowsToSheetMatrix(
  columns: GridImportColumn[],
  rows: Record<string, unknown>[]
): (string | number)[][] {
  const header = columns.map(formatImportColumnHeader)
  const body = rows.map((row) =>
    columns.map((col) => cellValueFromRow(row, col.field))
  )
  return [header, ...body]
}

export type SheetCellTheme = {
  background: string
  fontColor: string
}

/** Univer 单元格：写入值时同步带上底色/字色，避免暗色主题下仍落默认白底 */
export function toStyledCellValue(
  value: string | number,
  theme: SheetCellTheme
) {
  return {
    v: value,
    s: {
      bg: { rgb: theme.background },
      cl: { rgb: theme.fontColor },
    },
  }
}

export function toUniverStyleData(theme: SheetCellTheme) {
  return {
    bg: { rgb: theme.background },
    cl: { rgb: theme.fontColor },
  }
}

export function rowsToThemedSheetMatrix(
  columns: GridImportColumn[],
  rows: Record<string, unknown>[],
  theme: SheetCellTheme
) {
  const header = columns.map((col) =>
    toStyledCellValue(formatImportColumnHeader(col), theme)
  )
  const body = rows.map((row) =>
    columns.map((col) =>
      toStyledCellValue(cellValueFromRow(row, col.field), theme)
    )
  )
  return [header, ...body]
}

/**
 * @deprecated 暗色应使用 rowsToSheetMatrix（无样式）。
 * Univer darkMode 会对颜色 invert，显式浅色字会变成近黑。
 */
export function rowsToFontOnlySheetMatrix(
  columns: GridImportColumn[],
  rows: Record<string, unknown>[],
  _fontColor: string
) {
  return rowsToSheetMatrix(columns, rows)
}

/** 读取页面是否处于暗色（html.dark / data-z-ui-theme） */
export function resolveDocumentDark(): boolean {
  if (typeof document === 'undefined') return false
  const root = document.documentElement
  if (root.classList.contains('dark')) return true
  return root.getAttribute('data-z-ui-theme') === 'dark'
}

export function normalizeCellValue(raw: unknown): unknown {
  if (raw == null) return ''
  if (typeof raw === 'object' && raw !== null && 'v' in raw) {
    return (raw as { v: unknown }).v ?? ''
  }
  return raw
}

function isEmptyDataRow(line: unknown[], colCount: number): boolean {
  for (let index = 0; index < colCount; index += 1) {
    const value = normalizeCellValue(line[index])
    if (value !== '' && value != null) return false
  }
  return true
}

export function sheetMatrixToRows(
  columns: GridImportColumn[],
  matrix: unknown[][],
  options?: { maxRows?: number }
): Record<string, unknown>[] {
  const maxRows = options?.maxRows ?? 5000
  const rows: Record<string, unknown>[] = []

  for (
    let rowIndex = UNIVER_HEADER_ROW_COUNT;
    rowIndex < matrix.length;
    rowIndex += 1
  ) {
    const line = matrix[rowIndex]
    if (!line || isEmptyDataRow(line, columns.length)) {
      if (rows.length > 0) break
      continue
    }

    const record: Record<string, unknown> = {}
    columns.forEach((column, columnIndex) => {
      record[column.field] = normalizeCellValue(line[columnIndex])
    })
    rows.push(record)
    if (rows.length >= maxRows) break
  }

  return rows
}

export function resolveErrorCellA1(
  error: ImportRowError,
  columns: GridImportColumn[]
): string | null {
  const columnIndex = columns.findIndex(
    (column) => column.field === error.field
  )
  if (columnIndex < 0) return null
  const rowNumber = error.rowIndex + UNIVER_HEADER_ROW_COUNT
  return `${columnIndexToLetters(columnIndex)}${rowNumber}`
}

/** 无 field 时高亮整行（服务端 failureRows 等场景） */
export function resolveErrorRowRangeA1(
  error: ImportRowError,
  columns: GridImportColumn[]
): string | null {
  if (!columns.length || error.rowIndex < 1) return null
  const rowNumber = error.rowIndex + UNIVER_HEADER_ROW_COUNT
  const endCol = columnIndexToLetters(columns.length - 1)
  return `A${rowNumber}:${endCol}${rowNumber}`
}

/** 优先定位到具体单元格，否则回退为整行高亮 */
export function resolveErrorHighlightRange(
  error: ImportRowError,
  columns: GridImportColumn[]
): string | null {
  if (error.field) {
    const cell = resolveErrorCellA1(error, columns)
    if (cell) return cell
  }
  return resolveErrorRowRangeA1(error, columns)
}

export function formatErrorMessageLabel(error: ImportRowError): string {
  const label = error.title || error.field
  return label ? `${label}：${error.message}` : error.message
}

/** 按数据行聚合校验消息（rowIndex 为 1-based 数据行号） */
export function buildRowErrorMessages(
  errors: ImportRowError[],
  dataRowCount: number
): string[] {
  const messages = Array.from({ length: Math.max(dataRowCount, 0) }, () => '')
  errors.forEach((error) => {
    if (error.rowIndex < 1) return
    const index = error.rowIndex - 1
    if (index >= messages.length) return
    const piece = formatErrorMessageLabel(error)
    messages[index] = messages[index] ? `${messages[index]}；${piece}` : piece
  })
  return messages
}

export function resolveCssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim()
  return value || fallback
}

function parseColorToRgb(color: string): [number, number, number] | null {
  const value = color.trim()
  const shortHex = /^#([0-9a-fA-F]{3})$/.exec(value)
  if (shortHex) {
    const [r, g, b] = shortHex[1]
      .split('')
      .map((char) => Number.parseInt(char + char, 16))
    return [r, g, b]
  }
  const fullHex = /^#([0-9a-fA-F]{6})$/.exec(value)
  if (fullHex) {
    const n = Number.parseInt(fullHex[1], 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(value)
  if (rgb) {
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])]
  }
  return null
}

/** 感知亮度，用于避免暗色模式下误用浅色背景导致白字不可见 */
export function isLightColor(color: string): boolean {
  const rgb = parseColorToRgb(color)
  if (!rgb) return false
  const [r, g, b] = rgb
  return 0.299 * r + 0.587 * g + 0.114 * b > 180
}

function isTransparentColor(color: string): boolean {
  const value = color.trim().toLowerCase()
  if (!value || value === 'transparent') return true
  const rgba = /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([0-9.]+)\s*\)$/.exec(
    value
  )
  if (rgba) return Number(rgba[1]) === 0
  return false
}

/**
 * 根据容器及其祖先背景亮度判断是否处于暗色界面。
 * 宿主（如 MES）可能不用 html.dark，但仍是深色布局。
 */
export function resolveElementDark(el?: HTMLElement | null): boolean {
  if (typeof getComputedStyle === 'undefined' || !el) return false
  let node: HTMLElement | null = el
  while (node) {
    const bg = getComputedStyle(node).backgroundColor
    if (!isTransparentColor(bg)) {
      return !isLightColor(bg)
    }
    node = node.parentElement
  }
  return false
}

export function getSheetValidationColors(isDark = false) {
  // 暗色模式固定高对比色，避免 CSS 变量未切换时把浅色底写进单元格
  if (isDark) {
    return {
      cellBackground: '#141414',
      // 仅作回退值：暗色预览不写进单元格。Univer darkMode 会 invert 显式色，
      // 写 #ffffff 会反成近黑；应留空让默认黑字反成白字。
      cellFontColor: '#ffffff',
      errorBackground: resolveCssVar('--z-color-danger-light-9', '#5c2525'),
      errorFontColor: resolveCssVar('--z-color-danger', '#f78989'),
    }
  }

  const cellBackgroundFallback = '#ffffff'
  const cellFontColorFallback = '#303133'
  const errorBackgroundFallback = '#fde2e2'
  const errorFontColorFallback = '#c45656'

  let cellBackground = resolveCssVar('--z-bg-color', cellBackgroundFallback)
  let cellFontColor = resolveCssVar(
    '--z-text-color-primary',
    cellFontColorFallback
  )

  // 浅色模式必须保证浅底深字；若 CSS 变量仍是暗色 token 则回退
  if (!isLightColor(cellBackground)) {
    cellBackground = cellBackgroundFallback
  }
  if (isLightColor(cellFontColor)) {
    cellFontColor = cellFontColorFallback
  }

  return {
    cellBackground,
    cellFontColor,
    errorBackground: resolveCssVar(
      '--z-color-danger-light-9',
      errorBackgroundFallback
    ),
    errorFontColor: resolveCssVar('--z-color-danger', errorFontColorFallback),
  }
}
